import { openDB, type DBSchema, type IDBPDatabase } from "idb";

const DB_NAME = "kablan_inbox_db";
const DB_VERSION = 1;

export type SyncStatus = "pending" | "syncing" | "synced" | "error";

export interface OfflineInboxSubmission {
  id: string;
  token: string;
  submitterName: string;
  submitterPhone?: string;
  submitterEmail?: string;
  message: string;
  category: string;
  ref?: string; // Customization reference
  photoBlob?: Blob;
  photoFilename?: string;
  createdAt: string;
  syncStatus: SyncStatus;
  syncError?: string;
  lastSyncAttempt?: string;
  retryCount: number;
  // Store project name for offline display
  projectName?: string;
}

interface InboxDBSchema extends DBSchema {
  pending_submissions: {
    key: string;
    value: OfflineInboxSubmission;
    indexes: {
      "by-token": string;
      "by-status": SyncStatus;
    };
  };
  project_cache: {
    key: string; // token
    value: {
      token: string;
      projectName: string;
      cachedAt: string;
    };
  };
}

let dbInstance: IDBPDatabase<InboxDBSchema> | null = null;

/**
 * Get the IndexedDB database instance
 */
export async function getInboxDB(): Promise<IDBPDatabase<InboxDBSchema>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<InboxDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("pending_submissions")) {
        const store = db.createObjectStore("pending_submissions", {
          keyPath: "id",
        });
        store.createIndex("by-token", "token");
        store.createIndex("by-status", "syncStatus");
      }

      if (!db.objectStoreNames.contains("project_cache")) {
        db.createObjectStore("project_cache", { keyPath: "token" });
      }
    },
  });

  return dbInstance;
}

// ============================================
// Project Cache (for offline display)
// ============================================

/**
 * Cache project info for offline access
 */
export async function cacheProjectInfo(
  token: string,
  projectName: string
): Promise<void> {
  const db = await getInboxDB();
  await db.put("project_cache", {
    token,
    projectName,
    cachedAt: new Date().toISOString(),
  });
}

/**
 * Get cached project info
 */
export async function getCachedProjectInfo(
  token: string
): Promise<{ projectName: string } | undefined> {
  const db = await getInboxDB();
  const cached = await db.get("project_cache", token);
  if (cached) {
    return { projectName: cached.projectName };
  }
  return undefined;
}

// ============================================
// Submission Queue Operations
// ============================================

/**
 * Save a submission for offline sync
 */
export async function saveSubmissionOffline(
  submission: Omit<OfflineInboxSubmission, "id" | "syncStatus" | "retryCount" | "createdAt">
): Promise<OfflineInboxSubmission> {
  const db = await getInboxDB();
  const offlineSubmission: OfflineInboxSubmission = {
    ...submission,
    id: `offline_${crypto.randomUUID()}`,
    createdAt: new Date().toISOString(),
    syncStatus: "pending",
    retryCount: 0,
  };
  await db.put("pending_submissions", offlineSubmission);
  return offlineSubmission;
}

/**
 * Get all pending submissions for a token
 */
export async function getPendingSubmissionsByToken(
  token: string
): Promise<OfflineInboxSubmission[]> {
  const db = await getInboxDB();
  return db.getAllFromIndex("pending_submissions", "by-token", token);
}

/**
 * Get all pending submissions
 */
export async function getAllPendingSubmissions(): Promise<OfflineInboxSubmission[]> {
  const db = await getInboxDB();
  const all = await db.getAll("pending_submissions");
  return all.filter(
    (s) => s.syncStatus === "pending" || (s.syncStatus === "error" && s.retryCount < 3)
  );
}

/**
 * Update submission sync status
 */
export async function updateSubmissionSyncStatus(
  id: string,
  status: SyncStatus,
  error?: string
): Promise<void> {
  const db = await getInboxDB();
  const submission = await db.get("pending_submissions", id);
  if (submission) {
    submission.syncStatus = status;
    submission.lastSyncAttempt = new Date().toISOString();
    if (error) {
      submission.syncError = error;
      submission.retryCount += 1;
    } else if (status === "synced") {
      submission.syncError = undefined;
    }
    await db.put("pending_submissions", submission);
  }
}

/**
 * Remove a synced submission
 */
export async function removeSyncedSubmission(id: string): Promise<void> {
  const db = await getInboxDB();
  await db.delete("pending_submissions", id);
}

/**
 * Get a submission by ID
 */
export async function getSubmissionById(
  id: string
): Promise<OfflineInboxSubmission | undefined> {
  const db = await getInboxDB();
  return db.get("pending_submissions", id);
}

/**
 * Get pending count
 */
export async function getPendingSubmissionCount(): Promise<number> {
  const db = await getInboxDB();
  const pending = await db.getAllFromIndex("pending_submissions", "by-status", "pending");
  return pending.length;
}

// ============================================
// Sync Logic
// ============================================

const API_URL = typeof window !== "undefined"
  ? (import.meta.env.VITE_API_URL || "http://localhost:8787")
  : "";

let isSyncing = false;
let syncTimeout: ReturnType<typeof setTimeout> | null = null;

// Event listeners
const listeners: Set<(status: InboxSyncStatus) => void> = new Set();

export interface InboxSyncStatus {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime?: string;
  lastError?: string;
}

/**
 * Subscribe to sync status changes
 */
export function subscribeInboxSyncStatus(
  callback: (status: InboxSyncStatus) => void
): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Notify listeners of status change
 */
async function notifyListeners(): Promise<void> {
  const pendingCount = await getPendingSubmissionCount();
  const status: InboxSyncStatus = {
    isSyncing,
    pendingCount,
    lastSyncTime: new Date().toISOString(),
  };
  listeners.forEach((listener) => listener(status));
}

/**
 * Initialize inbox sync (call on page load)
 */
export function initInboxSync(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  // Initial sync if online
  if (navigator.onLine) {
    scheduleSyncWithDelay(2000);
  }
}

/**
 * Cleanup sync listeners
 */
export function cleanupInboxSync(): void {
  if (typeof window === "undefined") return;

  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
}

function handleOnline(): void {
  console.log("[InboxSync] Back online, scheduling sync...");
  scheduleSyncWithDelay(2000);
}

function handleOffline(): void {
  console.log("[InboxSync] Went offline");
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
}

function scheduleSyncWithDelay(delayMs: number): void {
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }
  syncTimeout = setTimeout(() => {
    syncAllSubmissions();
  }, delayMs);
}

/**
 * Trigger immediate sync
 */
export async function triggerInboxSync(): Promise<void> {
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
  await syncAllSubmissions();
}

/**
 * Sync all pending submissions
 */
async function syncAllSubmissions(): Promise<void> {
  if (isSyncing) {
    console.log("[InboxSync] Already syncing, skipping...");
    return;
  }

  if (!navigator.onLine) {
    console.log("[InboxSync] Offline, skipping sync");
    return;
  }

  isSyncing = true;
  await notifyListeners();

  try {
    const submissions = await getAllPendingSubmissions();
    console.log(`[InboxSync] Syncing ${submissions.length} submissions...`);

    for (const submission of submissions) {
      await syncSubmission(submission);
    }

    console.log("[InboxSync] Sync complete!");
  } catch (error) {
    console.error("[InboxSync] Sync failed:", error);
  } finally {
    isSyncing = false;
    await notifyListeners();
  }
}

/**
 * Sync a single submission
 */
async function syncSubmission(submission: OfflineInboxSubmission): Promise<{
  success: boolean;
  referenceNumber?: string;
}> {
  try {
    await updateSubmissionSyncStatus(submission.id, "syncing");

    const endpoint = submission.photoBlob
      ? `${API_URL}/api/inbox/${submission.token}/submit-with-photo`
      : `${API_URL}/api/inbox/${submission.token}/submit`;

    let response: Response;

    if (submission.photoBlob) {
      const formData = new FormData();
      formData.append("submitterName", submission.submitterName);
      if (submission.submitterPhone) formData.append("submitterPhone", submission.submitterPhone);
      if (submission.submitterEmail) formData.append("submitterEmail", submission.submitterEmail);
      formData.append("message", submission.message);
      formData.append("category", submission.category);
      formData.append("photo", submission.photoBlob, submission.photoFilename || "photo.jpg");
      if (submission.ref) formData.append("ref", submission.ref);

      response = await fetch(endpoint, {
        method: "POST",
        body: formData,
      });
    } else {
      response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submitterName: submission.submitterName,
          submitterPhone: submission.submitterPhone || undefined,
          submitterEmail: submission.submitterEmail || undefined,
          message: submission.message,
          category: submission.category,
          ref: submission.ref || undefined,
        }),
      });
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to submit");
    }

    const result = await response.json();
    console.log(`[InboxSync] Submission ${submission.id} synced: ${result.referenceNumber}`);

    await updateSubmissionSyncStatus(submission.id, "synced");
    await removeSyncedSubmission(submission.id);

    return { success: true, referenceNumber: result.referenceNumber };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error(`[InboxSync] Failed to sync ${submission.id}:`, errorMessage);
    await updateSubmissionSyncStatus(submission.id, "error", errorMessage);
    return { success: false };
  }
}

/**
 * Check if we're online
 */
export function isOnline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine;
}

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
}
